import { useEffect, useState } from "react";
import { CheckCircle2, MessageCircle, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { getProposalTexts, type ProposalLanguage } from "./i18n";

export function ProposalFeedbackSection({
  onSubmit,
  isPending,
  language,
  defaultName,
  defaultEmail,
}: {
  onSubmit: (input: { message: string; name?: string; email?: string }) => void;
  isPending: boolean;
  language: ProposalLanguage;
  defaultName?: string;
  defaultEmail?: string;
}) {
  const [message, setMessage] = useState("");
  const [name, setName] = useState(defaultName ?? "");
  const [email, setEmail] = useState(defaultEmail ?? "");
  const [sent, setSent] = useState(false);

  useEffect(() => {
    setName((current) => (current ? current : defaultName ?? ""));
  }, [defaultName]);
  useEffect(() => {
    setEmail((current) => (current ? current : defaultEmail ?? ""));
  }, [defaultEmail]);
  const tx = getProposalTexts(language);
  const t = tx.feedback;

  return (
    <section
      id="client-feedback"
      className="mx-auto max-w-5xl px-5 pb-12 pt-12 sm:px-10 sm:pb-24 sm:pt-20"
    >
      <div className="grid gap-8 sm:gap-10 lg:grid-cols-[1.1fr_1fr]">
        <div>
          <h2 className="max-w-2xl font-display text-[28px] font-extrabold leading-[1.05] tracking-display sm:text-5xl sm:leading-[1.02]">
            {t.headlinePart1}{" "}
            <span className="brand-gradient-text">{t.headlinePart2}</span>
          </h2>
          <p className="mt-4 max-w-xl text-sm leading-relaxed text-muted-foreground sm:text-base">
            {t.intro}
          </p>
          <ul className="mt-6 space-y-2 text-sm text-muted-foreground">
            <li className="flex items-start gap-2">
              <MessageCircle className="mt-0.5 h-4 w-4 shrink-0 text-brand-blue" />
              <span>{t.bullet1}</span>
            </li>
            <li className="flex items-start gap-2">
              <Sparkles className="mt-0.5 h-4 w-4 shrink-0 text-brand-blue" />
              <span>{t.bullet2}</span>
            </li>
          </ul>
        </div>

        <div className="rounded-2xl border border-border bg-white p-6 shadow-sm sm:p-8">
          {sent ? (
            <div className="py-8 text-center">
              <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-emerald-50">
                <CheckCircle2 className="h-6 w-6 text-emerald-600" />
              </div>
              <h3 className="font-display text-xl font-bold tracking-display text-foreground">
                {t.sentTitle}
              </h3>
              <p className="mx-auto mt-2 max-w-sm text-sm text-muted-foreground">
                {t.sentBody}
              </p>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setSent(false)}
                className="mt-5"
              >
                {t.writeMore}
              </Button>
            </div>
          ) : (
            <form
              onSubmit={(e) => {
                e.preventDefault();
                if (!message.trim() || isPending) return;
                onSubmit({
                  message: message.trim(),
                  name: name.trim() || undefined,
                  email: email.trim() || undefined,
                });
                setSent(true);
                setMessage("");
                setName(defaultName ?? "");
                setEmail(defaultEmail ?? "");
              }}
              className="space-y-4"
            >
              <div>
                <Label
                  htmlFor="feedback-message"
                  className="text-xs font-semibold uppercase tracking-[0.14em] text-muted-foreground"
                >
                  {t.commentLabel}
                </Label>
                <Textarea
                  id="feedback-message"
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder={t.commentPlaceholder}
                  className="mt-1.5 min-h-[140px] text-base"
                  required
                />
              </div>
              <div className="grid gap-3 sm:grid-cols-2">
                <div>
                  <Label
                    htmlFor="feedback-name"
                    className="text-xs font-semibold uppercase tracking-[0.14em] text-muted-foreground"
                  >
                    {t.nameLabel}
                  </Label>
                  <Input
                    id="feedback-name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder={t.namePlaceholder}
                    className="mt-1.5"
                  />
                </div>
                <div>
                  <Label
                    htmlFor="feedback-email"
                    className="text-xs font-semibold uppercase tracking-[0.14em] text-muted-foreground"
                  >
                    {t.emailLabel}
                  </Label>
                  <Input
                    id="feedback-email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder={t.emailPlaceholder}
                    className="mt-1.5"
                  />
                </div>
              </div>
              <Button
                type="submit"
                size="lg"
                disabled={!message.trim() || isPending}
                className="w-full bg-brand-ink text-white hover:bg-brand-ink/90"
              >
                {isPending ? t.sending : t.submit}
              </Button>
              <p className="text-[11px] leading-relaxed text-muted-foreground">
                {t.legal}
              </p>
            </form>
          )}
        </div>
      </div>
    </section>
  );
}
